import type { AnchorHTMLAttributes, MouseEvent, ReactNode } from "react";
import type { NavigateOptions } from "react-router-dom";
import { useOpenAppNavigation } from "@/hooks/useOpenAppNavigation";
import type { AppNavigationTarget } from "@/lib/navigation/appNavigation";

interface AppNavigationLinkProps extends Omit<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  "href" | "onClick" | "target"
> {
  /** Where the link goes; `target` is taken by the anchor attribute, hence `to`. */
  to: AppNavigationTarget;
  /** Router options for a plain click, e.g. the state a workspace reads its way back from. */
  navigateOptions?: NavigateOptions;
  children: ReactNode;
}

/**
 * A real link to an in-app target: the href makes cmd-click, middle-click and "copy link"
 * work like any anchor, while a plain click goes through `openAppNavigation` so the
 * navigation stack and viewer state are handled the same way as everywhere else.
 */
export const AppNavigationLink = ({
  to,
  navigateOptions,
  children,
  ...anchorProps
}: AppNavigationLinkProps) => {
  const { openNavigation, buildNavigationUrl } = useOpenAppNavigation();

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    openNavigation(to, event, { navigateOptions });
  };

  return (
    <a
      {...anchorProps}
      href={buildNavigationUrl(to) ?? undefined}
      onClick={handleClick}
    >
      {children}
    </a>
  );
};

export default AppNavigationLink;
